<script setup lang="ts">
const { attachmentUrl, staticUrl } = useImageUrl();

interface Props {
  bundle: Record<string, unknown>;
  home?: boolean;
  maker?: Record<string, unknown>;
  reviews?: Record<string, unknown>[] | boolean;
  isLazy?: boolean;
  isCarousel?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  home: false,
  maker: () => ({}),
  reviews: () => [],
  isLazy: false,
  isCarousel: false,
});

const emit = defineEmits<{
  returnBundle: [payload: { quantity: number; product: Record<string, unknown> }];
}>();

const hasProfileImage = computed(() => {
  const maker = props.maker as Record<string, unknown>;
  const attachments = maker.attachments as Record<string, unknown[]> | undefined;
  return attachments?.logo && attachments.logo.length > 0;
});

const reviewCount = computed(() => {
  if (!props.reviews || !Array.isArray(props.reviews)) return "New";
  const totalScore = props.reviews.reduce(
    (sum: number, review: Record<string, unknown>) => sum + Number(review.score || 0),
    0,
  );
  return props.reviews.length
    ? (totalScore / props.reviews.length).toFixed(1)
    : "New";
});

const bundleImage = computed(() => {
  const b = props.bundle as Record<string, unknown>;
  const attachments = b.attachments as Record<string, unknown[]> | undefined;
  if (attachments?.mainImage?.length) {
    return attachmentUrl(attachments.mainImage[0] as Record<string, unknown>, "big");
  }
  return staticUrl("/Images/site-images/placeholder.png");
});

const profileImage = computed(() => {
  const maker = props.maker as Record<string, unknown>;
  const attachments = maker.attachments as Record<string, unknown[]> | undefined;
  if (attachments?.profile?.length) {
    return attachmentUrl(attachments.profile[0] as Record<string, unknown>, "small");
  }
  return "";
});

function openBundle() {
  const b = props.bundle as Record<string, unknown>;
  emit("returnBundle", {
    quantity: b.min_order_amount ? Number(b.min_order_amount) : 1,
    product: b,
  });
}

function onImageError(e: Event) {
  (e.target as HTMLImageElement).src = staticUrl("/Images/site-images/placeholder.png");
}
</script>

<template>
  <div class="bundle-list">
    <div class="bundle-list__list">
      <div
        class="bundle-list__list--container"
        :style="
          home
            ? 'height: 10rem;'
            : 'flex-direction: column-reverse;'
        "
        @click="openBundle"
      >
        <div
          class="details"
          :style="home ? 'width: 60%' : 'width: 90%'"
        >
          <p class="details__title grey-text">
            {{ bundle.name }}
          </p>
          <div class="details__info">
            <div class="details__info--dietries">
              <div class="servings">
                <Badge
                  v-if="Number(bundle.min_order_amount) > 1"
                  variant="default"
                  size="sm"
                >
                  Min. {{ bundle.min_order_amount }}
                </Badge>
                <Badge
                  v-if="Number(bundle.servings) > 1"
                  variant="default"
                  size="sm"
                >
                  Serves {{ bundle.servings }}
                </Badge>
                <Badge
                  v-if="Number(bundle.pieces) > 1"
                  variant="default"
                  size="sm"
                >
                  {{ bundle.pieces }} pieces
                </Badge>
                <Badge
                  v-if="bundle.individually_packaged"
                  variant="default"
                  size="sm"
                >
                  Individually packaged
                </Badge>
              </div>
            </div>
            <div class="details__info--price">
              <p class="grey-text details__info--price-amount">
                £{{ parseFloat(String(Number(bundle.price_gross) / 100)).toFixed(2) }}
              </p>
              <p class="light-grey-text small-text">/ per person</p>
            </div>
          </div>
        </div>
        <div
          class="image"
          :style="
            home
              ? 'width: 35%; margin: 0 0 0 1%;'
              : 'width: 100%; margin: 0; overflow: hidden; aspect-ratio: 3 / 2;'
          "
        >
          <img
            :loading="isLazy ? 'lazy' : 'eager'"
            decoding="async"
            :style="
              home
                ? 'border-radius: 0 0.4rem 0.4rem 0;'
                : 'width: 100%; height: 100%; object-fit: cover; border-radius: 0.4rem 0.4rem 0 0;'
            "
            :src="bundleImage"
            :alt="String(bundle.name || '')"
            :height="home ? '300' : '100%'"
            @error="onImageError"
          />
        </div>
      </div>
    </div>
    <div
      v-if="home"
      class="bundle-list__profile"
    >
      <div
        v-if="hasProfileImage"
        class="bundle-list__profile--profile-image"
        :class="(maker as Record<string, unknown>).is_active === false && 'opacity'"
        :style="`background-image: url('${profileImage}')`"
      />
      <p class="bundle-list__profile--image-title grey-text">
        {{ (maker as Record<string, unknown>).name }}
      </p>
      <div
        v-if="((maker as Record<string, unknown>).cuisines as { name: string }[] | undefined)?.length"
        class="bundle-list__profile--cuisines"
      >
        <Badge
          v-for="cuisine in ((maker as Record<string, unknown>).cuisines as { name: string }[]).slice(0, 3)"
          :key="cuisine.name"
          variant="default"
          size="sm"
        >
          {{ cuisine.name }}
        </Badge>
      </div>
      <div class="bundle-list__profile--rating">
        <fa
          v-for="(number, i) in 5"
          :key="`star_${i}`"
          :icon="['fas', 'star']"
          :class="Number(reviewCount) > i ? 'yellow-text' : 'light-grey-text'"
        />
        <span class="small-text light-grey-text">({{ reviewCount }})</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
$laptop: 1200px;
$tablet: 768px;
$mobile: 600px;

.bundle-list {
  width: 98%;
  @media (max-width: $mobile) {
    width: 100%;
  }
  // Hover: lift via shadow + translate (matches MakerList card hover so
  // both card families behave the same way). Replaces the old
  // `opacity: 0.8` which felt washed out.
  &:hover {
    .bundle-list__list {
      box-shadow: 0 0.25rem 0.75rem rgba(0, 0, 0, 0.15);
      transform: translateY(-0.125rem);
      .image img {
        transform: scale(1.04);
      }
    }
  }
  &__profile {
    cursor: pointer;
    float: left;
    position: relative;
    margin: 0 2%;
    border-radius: 0.4rem;
    transition: transform 0.2s;
    width: 96%;
    &:hover {
      opacity: 0.8;
    }
    a {
      text-decoration: none;
    }
    h2 {
      margin-bottom: 0 !important;
    }
    h4 {
      margin: 0 0 1rem;
    }
    p {
      font-weight: var(--weight-light) !important;
    }
    &--image-title {
      margin: 15px 0 0 !important;
      width: 75%;
    }
    &--image-subtitle {
      margin: 3px 0;
      width: 75%;
      font-weight: var(--weight-light);
      @media (max-width: $laptop) {
        width: 85%;
      }
    }
    &--cuisines {
      display: flex;
      flex-wrap: wrap;
      column-gap: 4px;
      row-gap: 4px;
      width: 75%;
      margin: 6px 0 0;
      @media (max-width: $laptop) {
        width: 85%;
      }
    }
    &--profile-image {
      float: right;
      width: 3rem;
      height: 3rem;
      border-radius: 50%;
      overflow: hidden;
      margin-top: 1.5rem;
      position: relative;
      background-position: center;
      background-size: contain;
    }
    &--rating {
      display: flex;
      align-items: center;
      svg {
        font-size: 0.7rem;
      }
      span {
        margin: 0 0.5rem !important;
      }
    }
  }
  &__list {
    display: flex;
    flex-direction: column;
    margin: 0 1%;
    // Match the maker-list card chrome: larger radius, thin neutral
    // border, soft shadow, white surface. Reads as the same card family
    // as MakerList instead of a heavier outlined block.
    border-radius: var(--radius);
    cursor: pointer;
    border: 0.0625rem solid var(--color-grey-3);
    box-shadow: var(--shadow-1);
    overflow: hidden;
    background-color: var(--color-white-1);
    transition:
      box-shadow var(--dur-base) var(--ease-out),
      transform var(--dur-base) var(--ease-out);
    @media (max-width: $tablet) {
      width: 96%;
    }
    @media (max-width: $mobile) {
      width: 99%;
    }
    &--container {
      align-items: center;
      display: flex;
      justify-content: space-between;
      width: 100%;
      .image {
        display: flex;
        align-items: center;
        overflow: hidden;
        img {
          transition: transform 500ms var(--ease-out);
        }
      }
      .details {
        padding: 1rem 5% 1.25rem;
        display: flex;
        flex-direction: column;
        // Even spacing between title → badges → price via a single gap.
        // Previously `space-between` pinned title to the top and the
        // info block to the bottom, producing a giant void in the
        // middle of taller cards (the carousel-mode card is 18rem).
        justify-content: flex-start;
        gap: 0.75rem;
        background-color: var(--color-white-1);
        p {
          margin: 0;
          padding: 0;
        }
        // Bundle title matches the maker-list card title so the two card
        // families share the same headline rhythm — semibold, tight
        // letter-spacing, single line with ellipsis.
        &__title {
          font-size: 0.9rem;
          line-height: 1.2;
          font-weight: var(--weight-semibold);
          letter-spacing: -0.01em;
          color: var(--color-grey-1) !important;
          margin: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        &__info {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          &--dietries {
            display: flex;
            flex-direction: column;
            .servings {
              display: flex;
              flex-wrap: wrap;
              gap: 0.4rem;
              width: 100%;
            }
          }
          &--price {
            display: flex;
            align-items: center;
            gap: 0.4rem;
            // Price is the prominent line in the details column — bumped
            // up + semibold + neutral ink (was bright pink-1 heavy). Lets
            // the colour palette of the card stay neutral and lets the
            // image carry the visual weight, like maker-list.
            &-amount {
              font-size: 1.1rem;
              font-weight: var(--weight-semibold);
              color: var(--color-grey-1) !important;
              line-height: 1;
            }
            h4 {
              margin: 0 !important;
              display: flex;
              justify-content: flex-end;
              align-items: center;
            }
          }
        }
      }
    }
  }
}
</style>
